/**
 * The palette's host (S6): which page registered palette providers, and their context. The
 * mechanism is S5's tool host (toolHostStore.js): a page registers while it is mounted, the
 * shell reads the registration; nothing registered, nothing added. The shell's own providers
 * (shellProviders.js) are always on and are not registered here.
 *
 * A registration is { module, providers: [(ctx) -> entries], ctx }. ctx should be memoised;
 * providers are called when the palette renders, so they read live state (the tool host's open
 * tool, say) at that moment rather than subscribing the page to it.
 */
import { createContext, createElement, useContext, useLayoutEffect, useState, useSyncExternalStore } from 'react';

const INITIAL = Object.freeze({ registration: null });

export function createPaletteHostStore() {
  let state = INITIAL;
  const listeners = new Set();
  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    setRegistration(registration) {
      const next = registration || null;
      if (next === state.registration) return;
      state = { registration: next };
      listeners.forEach((fn) => fn());
    }
  };
}

const PaletteHostContext = createContext(null);

/** MainLayout wraps the shell in this; pages register through usePaletteHost. */
export function PaletteHostProvider({ children }) {
  const [store] = useState(createPaletteHostStore);
  return createElement(PaletteHostContext.Provider, { value: store }, children);
}

const EMPTY_STORE = { subscribe: () => () => {}, getState: () => INITIAL };

/** The registered page's providers and context, or null. */
export function usePaletteRegistration() {
  const store = useContext(PaletteHostContext) || EMPTY_STORE;
  return useSyncExternalStore(store.subscribe, store.getState, store.getState).registration;
}

/**
 * Register a page's palette providers for as long as it is mounted.
 * @param {{ module: string, providers: Array<Function>, ctx: object }} registration
 */
export function usePaletteHost({ module, providers, ctx }) {
  const store = useContext(PaletteHostContext);
  useLayoutEffect(() => {
    if (store) store.setRegistration({ module, providers, ctx });
  }, [store, module, providers, ctx]);
  useLayoutEffect(() => () => { if (store) store.setRegistration(null); }, [store]);
}
